import { QuackChat } from "@/components/quack-chat/QuackChat";
import { Message } from "@/components/quack-chat/core/entities/Message";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import Switch from "@/components/shared/inputs/switch";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { GetSystemMessageResponse } from "@/v2/entities/quackchat/Quackchat.api";
import {
  QuackChatSystemMessageType,
  quackChatSystemMessageTypesMap,
} from "@/v2/entities/quackchat/Quackchat.model";
import { DotsVerticalIcon } from "@radix-ui/react-icons";
import { Badge, Box, Button, Em, Flex, Table, Text } from "@radix-ui/themes";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { EditSystemMessage } from "./EditSystemMessage";
import ExitEditDialog from "./ExitEditDialog";
import { FilterRuleDialog } from "./FilterRuleDialog";
import {
  isMessageFilterKnownValue,
  MessageFilter,
  MessageFilterRule,
  MessageFilters,
  operatorOptions,
  isValuelessOperator,
  QuackChatSystemMessageOverrideProps,
} from "./SystemMessage.model";
import { QuackChatSystemMessageSidebar } from "./QuackChatSystemMessageSidebar";
import styles from "./QuackchatSystemMessageConfiguration.module.scss";
import {
  useGetQuackChatFieldOptions,
  useQuackChatSystemMessage,
} from "./useQuackChatSystemMessage";

export function QuackChatSystemMessageOverride({
  config,
  defaultMessageType,
  isEditing,
  simulatorId,
  onStartEdit,
  onStopEdit,
  onError,
}: QuackChatSystemMessageOverrideProps) {
  const [selectedMessageType, setSelectedMessageType] = useState<
    QuackChatSystemMessageType | undefined
  >(defaultMessageType);
  const [pendingMessageType, setPendingMessageType] = useState<
    QuackChatSystemMessageType | undefined
  >(undefined);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const {
    systemMessageWithConditions,
    refetchSystemMessageConditions,
    systemMessages,
    loadingSystemMessages,
    refetchSystemMessages,
    toggleSystemMessage,
    overrideSystemMessage,
    isSavingFilter,
  } = useQuackChatSystemMessage(selectedMessageType, simulatorId, onError);

  const fieldOptions = useGetQuackChatFieldOptions(selectedMessageType);

  const [messageFilters, setMessageFilters] = useState<MessageFilters>({});
  const [editingRule, setEditingRule] = useState<
    MessageFilterRule | null | undefined
  >(undefined);

  useEffect(() => {
    if (systemMessageWithConditions && selectedMessageType) {
      const rules: MessageFilterRule[] = systemMessageWithConditions.map(
        (rule) => ({
          id: rule.id,
          message: rule.message,
          conditions: rule.conditions || [],
        })
      );

      setMessageFilters((prev) => ({
        ...prev,
        [selectedMessageType]: rules,
      }));
    }
  }, [systemMessageWithConditions, selectedMessageType]);

  const systemMessageMap = useMemo(() => {
    const map = new Map<QuackChatSystemMessageType, GetSystemMessageResponse>();

    for (const message of systemMessages?.messages ?? []) {
      map.set(message.type, message);
    }

    return map;
  }, [systemMessages]);

  const systemMessage = useMemo(() => {
    if (selectedMessageType == null) {
      return;
    }

    return systemMessageMap.get(selectedMessageType) ?? null;
  }, [systemMessageMap, selectedMessageType]);

  const messages = useMemo<Message[]>(() => {
    if (systemMessage == null || systemMessage.enabled === false) {
      return [];
    }

    return [
      {
        action: "standard_message",
        author: "AUTO_AGENT",
        text: systemMessage.message,
        deduplicationKey: "edit_message",
        createdAt: "2021-09-01T12:00:00Z",
        data: {},
      },
    ];
  }, [systemMessage]);

  const handleMessageTypeSelected = useCallback(
    (type: QuackChatSystemMessageType) => {
      if (isEditing) {
        setPendingMessageType(type);
        return;
      }

      setSelectedMessageType(type);
    },
    [isEditing]
  );

  const toggleSystemMessageEnabled = useCallback(
    (enabled: boolean) => {
      if (selectedMessageType == null) {
        return;
      }

      toggleSystemMessage({
        type: selectedMessageType,
        enabled,
      });
    },
    [selectedMessageType, toggleSystemMessage]
  );

  const handleContinueMessageTypeChange = useCallback(() => {
    onStopEdit();
    setSelectedMessageType(pendingMessageType);
    setPendingMessageType(undefined);
  }, [onStopEdit, pendingMessageType]);

  const currentRules = useMemo(() => {
    if (selectedMessageType == null) {
      return [];
    }
    return messageFilters[selectedMessageType] ?? [];
  }, [messageFilters, selectedMessageType]);

  const handleRemoveFilter = useCallback(
    (ruleId: string) => {
      if (selectedMessageType == null) {
        return;
      }

      const currentRulesForType = messageFilters[selectedMessageType] ?? [];
      const ruleToDelete = currentRulesForType.find(
        (rule) => rule.id === ruleId
      );

      if (!ruleToDelete) {
        return;
      }

      const updatedRules = currentRulesForType.filter(
        (rule) => rule.id !== ruleId
      );

      setMessageFilters((prev) => ({
        ...prev,
        [selectedMessageType]: updatedRules,
      }));

      overrideSystemMessage(
        {
          type: selectedMessageType,
          message: ruleToDelete.message,
          filterRules: [],
          id: ruleToDelete.id,
        },
        {
          onSuccess: () => {
            toast.success("Filter rule deleted");
            refetchSystemMessageConditions();
          },
          onError: () => {
            setMessageFilters((prev) => ({
              ...prev,
              [selectedMessageType]: currentRulesForType,
            }));
          },
        }
      );
    },
    [
      selectedMessageType,
      messageFilters,
      overrideSystemMessage,
      refetchSystemMessageConditions,
    ]
  );

  const formatRuleConditions = useCallback((rule: MessageFilterRule) => {
    if (rule.conditions.length === 0) {
      return "Not configured";
    }
    return rule.conditions
      .filter(
        (condition) =>
          condition.field &&
          (condition.value || isValuelessOperator(condition.operator))
      )
      .map((condition) => {
        const operatorLabel =
          operatorOptions.find((opt) => opt.value === condition.operator)
            ?.label ?? condition.operator;
        if (isValuelessOperator(condition.operator)) {
          return `${condition.field} ${operatorLabel}`;
        }
        let displayValue = condition.value;
        if (isMessageFilterKnownValue(condition.value)) {
          if (condition.knownSourceFieldId) {
            const srcField = fieldOptions?.find(
              (opt) => opt.id === condition.knownSourceFieldId
            );
            displayValue = srcField
              ? `{${srcField.label}}`
              : condition.value;
          } else {
            const condField = fieldOptions?.find(
              (opt) => opt.id === condition.field
            );
            displayValue = condField
              ? `{${condField.label}}`
              : condition.value;
          }
        } else if (condition.operator === "like") {
          displayValue = displayValue.replaceAll("%", "");
        }
        return `${condition.field} ${operatorLabel} ${displayValue}`;
      })
      .join(" and ");
  }, [fieldOptions]);

  const handleSaveFilter = useCallback(
    (conditions: MessageFilter[], message: string) => {
      if (selectedMessageType == null) {
        return;
      }

      const existingRules = messageFilters[selectedMessageType] ?? [];
      const updatedRules = editingRule
        ? existingRules.map((rule) =>
            rule.id === editingRule.id ? { ...rule, message, conditions } : rule
          )
        : [...existingRules, { id: "", message, conditions }];

      setMessageFilters((prev) => ({
        ...prev,
        [selectedMessageType]: updatedRules,
      }));

      overrideSystemMessage(
        {
          type: selectedMessageType,
          message,
          filterRules: conditions.map((condition) => {
            const value =
              condition.operator === "like" &&
              !isMessageFilterKnownValue(condition.value)
                ? condition.value.replaceAll("%", "")
                : condition.value;
            return {
              id: condition.id,
              field: condition.field,
              operator: condition.operator,
              value,
              ...(condition.knownSourceFieldId
                ? { knownSourceFieldId: condition.knownSourceFieldId }
                : {}),
            };
          }),
          id: editingRule?.id,
        },
        {
          onSuccess: () => {
            toast.success(
              editingRule ? "Filter rule updated" : "Filter rule created"
            );
            setEditingRule(undefined);
            refetchSystemMessageConditions();
          },
          onError: () => {
            setMessageFilters((prev) => ({
              ...prev,
              [selectedMessageType]: existingRules,
            }));
          },
        }
      );
    },
    [
      selectedMessageType,
      messageFilters,
      editingRule,
      overrideSystemMessage,
      refetchSystemMessageConditions,
    ]
  );

  return (
    <>
      <Flex className={styles.QuackChatSystemMessageOverride}>
        <ExitEditDialog
          open={pendingMessageType != null}
          onCancel={() => setPendingMessageType(undefined)}
          onContinue={handleContinueMessageTypeChange}
        />
        <QuackChatSystemMessageSidebar
          selectedMessageType={selectedMessageType}
          handleMessageTypeSelected={handleMessageTypeSelected}
          allowToggle={true}
          systemMessageMap={systemMessageMap}
        />

        <Flex flexGrow={"1"} direction={"column"} className={styles.Preview}>
          {selectedMessageType != null && (
            <>
              <Flex py={"24px"} px={"40px"} direction={"column"} gap={"4"}>
                <Flex justify={"between"} gap={"2"}>
                  <Flex direction={"column"} gap={"1"} maxWidth={"600px"}>
                    <Text
                      size={"2"}
                      color={"gray"}
                      weight={"medium"}
                      highContrast
                    >
                      {
                        quackChatSystemMessageTypesMap[selectedMessageType]
                          .title
                      }
                    </Text>

                    <Text size={"2"} color={"gray"}>
                      {
                        quackChatSystemMessageTypesMap[selectedMessageType]
                          .description
                      }
                    </Text>
                  </Flex>

                  <Flex gap={"2"} align={"center"}>
                    <>
                      {currentRules.length > 0 && (
                        <Badge color={"blue"} size={"1"}>
                          {currentRules.length} rule
                          {currentRules.length !== 1 ? "s" : ""}
                        </Badge>
                      )}

                      <Button
                        size={"1"}
                        highContrast
                        variant={showAdvancedFilters ? "solid" : "soft"}
                        onClick={() =>
                          setShowAdvancedFilters(!showAdvancedFilters)
                        }
                      >
                        Advanced
                      </Button>
                    </>
                    <Switch
                      value={systemMessage?.enabled ?? false}
                      onChange={toggleSystemMessageEnabled}
                    />
                  </Flex>
                </Flex>

                {showAdvancedFilters && (
                  <Flex direction={"column"} gap={"3"}>
                    <Box>
                      <Flex justify={"between"} gap={"3"}>
                        <Flex direction={"column"} gap={"1"}>
                          <Text size={"2"} as={"label"}>
                            Filter Rules
                          </Text>
                          <Text size={"2"} color={"gray"}>
                            <Em>
                              Configure conditional messages based on context
                              fields
                            </Em>
                          </Text>
                        </Flex>
                        <Flex flexShrink={"0"} pr={"1"}>
                          <Button
                            variant="solid"
                            size="2"
                            highContrast
                            onClick={() => setEditingRule(null)}
                            disabled={fieldOptions?.length === 0}
                          >
                            + Add Filter Rule
                          </Button>
                        </Flex>
                      </Flex>
                    </Box>

                    <Flex direction={"column"} gap={"16px"}>
                      {currentRules.length === 0 ? (
                        <Box>
                          <Text size="2" color="gray" align="center">
                            No filter rules found. Create your first rule to get
                            started.
                          </Text>
                        </Box>
                      ) : (
                        <Table.Root style={{ width: "100%" }}>
                          <Table.Header>
                            <Table.Row>
                              <Table.ColumnHeaderCell width="90%">
                                Condition
                              </Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell
                                width="10%"
                                align="right"
                              />
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            {currentRules.map((rule) => (
                              <Table.Row key={rule.id}>
                                <Table.Cell width="90%">
                                  <Text weight="medium" highContrast>
                                    {formatRuleConditions(rule)}
                                  </Text>
                                </Table.Cell>
                                <Table.Cell width="10%" align="right">
                                  <Flex gap="2" justify="end" align="center">
                                    <DropdownMenu
                                      items={[
                                        {
                                          label: "Edit",
                                          onClick: () => setEditingRule(rule),
                                        },
                                        {
                                          label: "Delete",
                                          onClick: () =>
                                            handleRemoveFilter(rule.id),
                                        },
                                      ]}
                                      align="end"
                                    >
                                      <DropdownMenu.Trigger>
                                        <Button
                                          size="1"
                                          variant="ghost"
                                          color="gray"
                                        >
                                          <DotsVerticalIcon />
                                        </Button>
                                      </DropdownMenu.Trigger>
                                    </DropdownMenu>
                                  </Flex>
                                </Table.Cell>
                              </Table.Row>
                            ))}
                          </Table.Body>
                        </Table.Root>
                      )}
                    </Flex>
                  </Flex>
                )}
              </Flex>

              {!showAdvancedFilters && (
                <Flex flexGrow={"1"} justify={"center"} align={"center"}>
                  <QuackChat
                    config={config}
                    messages={messages}
                    thinking={loadingSystemMessages}
                  />

                  {selectedMessageType != null && messages?.length > 0 && (
                    <EditSystemMessage
                      type={selectedMessageType}
                      message={messages[0]}
                      isEditing={isEditing}
                      simulatorId={simulatorId}
                      onStartEdit={onStartEdit}
                      onStopEdit={onStopEdit}
                      onSuccess={refetchSystemMessages}
                    />
                  )}
                </Flex>
              )}
            </>
          )}
        </Flex>
      </Flex>

      <FilterRuleDialog
        open={editingRule !== undefined}
        editingRule={editingRule}
        isSavingFilter={isSavingFilter}
        onClose={() => setEditingRule(undefined)}
        onSave={handleSaveFilter}
        selectedMessageType={selectedMessageType}
      />
    </>
  );
}
